import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Calculatrice de moles en chimie";
const DESCRIPTION =
  "Calculatrice de moles en chimie. Calculateur de chimie en francais pour cours et TP, avec saisie simple, resultat rapide et interpretation claire.";
const KEYWORDS =
  "moles calculator, calculatrice chimie, formule de chimie, tp de chimie, calcul en ligne";
const OG_URL = "https://do-calculate.com/calculator/fr/moles-calculator/";

const AVOGADRO = 6.022e23;
const MOLAR_VOLUME_STP = 22.4;

const CALC_METHODS = {
  mass_to_moles: "De la masse ? moles",
  particles_to_moles: "Du nombre de particules ? moles",
  volume_to_moles: "Du volume de gaz (STP) ? moles",
  moles_to_mass: "Des moles ? masse",
  moles_to_particles: "Des moles ? nombre de particules",
  moles_to_volume: "Des moles ? volume de gaz (STP)",
};

const COMMON_SUBSTANCES = {
  custom: { name: "Substance personnalise", molarMass: 0 },
  H2O: { name: "Eau H2O", molarMass: 18.015 },
  NaCl: { name: "Chlorure de sodium NaCl", molarMass: 58.44 },
  CO2: { name: "Dioxyde de carbone CO2", molarMass: 44.01 },
  O2: { name: "Oxygne O2", molarMass: 32.0 },
  H2: { name: "Hydrogne H2", molarMass: 2.016 },
  N2: { name: "Azote N2", molarMass: 28.014 },
  CH4: { name: "Mthane CH4", molarMass: 16.043 },
  H2SO4: { name: "Acide sulfurique H2SO4", molarMass: 98.079 },
  HCl: { name: "Acide chlorhydrique HCl", molarMass: 36.461 },
  NaOH: { name: "Hydroxyde de sodium NaOH", molarMass: 40.0 },
  CaCO3: { name: "Carbonate de calcium CaCO3", molarMass: 100.087 },
  C6H12O6: { name: "Glucose C6H12O6", molarMass: 180.156 },
  Fe: { name: "Fer Fe", molarMass: 55.845 },
  Cu: { name: "Cuivre Cu", molarMass: 63.546 },
  Al: { name: "Aluminium Al", molarMass: 26.982 },
  Ca: { name: "Calcium Ca", molarMass: 40.078 },
  Mg: { name: "Magnsium Mg", molarMass: 24.305 },
  C: { name: "Carbone C", molarMass: 12.011 },
  Na: { name: "Sodium Na", molarMass: 22.99 },
};

function MolesCalculator() {
  const [calcMethod, setCalcMethod] = useState("mass_to_moles");
  const [substance, setSubstance] = useState("H2O");
  const [customMolarMass, setCustomMolarMass] = useState("");
  const [mass, setMass] = useState("");
  const [moles, setMoles] = useState("");
  const [particles, setParticles] = useState("");
  const [volume, setVolume] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const getMolarMass = () => {
    if (substance === "custom") return parseFloat(customMolarMass || 0);
    return COMMON_SUBSTANCES[substance]?.molarMass || 0;
  };

  const formatScientific = (num) => {
    if (num === 0) return "0";
    if (Math.abs(num) >= 1e6 || Math.abs(num) < 0.0001) return num.toExponential(4);
    return num.toLocaleString("fr-FR", { maximumFractionDigits: 6 });
  };

  const calculateResult = () => {
    try {
      setError("");
      const molarMass = getMolarMass();
      let resultData = null;

      switch (calcMethod) {
        case "mass_to_moles": {
          const massVal = parseFloat(mass);
          if (!mass || isNaN(massVal) || massVal <= 0) { setError("Veuillez entrer une masse valide et positive."); setResult(null); return; }
          if (molarMass <= 0) { setError("Veuillez entrer une masse molaire valide."); setResult(null); return; }
          const molesResult = massVal / molarMass;
          resultData = { moles: molesResult, particles: molesResult * AVOGADRO, volumeSTP: molesResult * MOLAR_VOLUME_STP, mass: massVal, molarMass, formula: `n = ${massVal} g  ${molarMass} g/mol = ${molesResult.toFixed(6)} mol` };
          break;
        }
        case "particles_to_moles": {
          const particlesVal = parseFloat(particles);
          if (!particles || isNaN(particlesVal) || particlesVal <= 0) { setError("Veuillez entrer un nombre de particules valide."); setResult(null); return; }
          const molesResult = particlesVal / AVOGADRO;
          resultData = { moles: molesResult, particles: particlesVal, volumeSTP: molesResult * MOLAR_VOLUME_STP, mass: molarMass > 0 ? molesResult * molarMass : null, molarMass: molarMass > 0 ? molarMass : null, formula: `n = ${particlesVal.toExponential(3)}  6,02210 = ${molesResult.toExponential(4)} mol` };
          break;
        }
        case "volume_to_moles": {
          const volumeVal = parseFloat(volume);
          if (!volume || isNaN(volumeVal) || volumeVal <= 0) { setError("Veuillez entrer un volume valide."); setResult(null); return; }
          const molesResult = volumeVal / MOLAR_VOLUME_STP;
          resultData = { moles: molesResult, particles: molesResult * AVOGADRO, volumeSTP: volumeVal, mass: molarMass > 0 ? molesResult * molarMass : null, molarMass: molarMass > 0 ? molarMass : null, formula: `n = ${volumeVal} L  22,4 L/mol = ${molesResult.toFixed(6)} mol` };
          break;
        }
        case "moles_to_mass": {
          const molesVal = parseFloat(moles);
          if (!moles || isNaN(molesVal) || molesVal <= 0) { setError("Veuillez entrer un nombre de moles valide."); setResult(null); return; }
          if (molarMass <= 0) { setError("Veuillez entrer une masse molaire valide."); setResult(null); return; }
          resultData = { moles: molesVal, particles: molesVal * AVOGADRO, volumeSTP: molesVal * MOLAR_VOLUME_STP, mass: molesVal * molarMass, molarMass, formula: `m = ${molesVal} mol  ${molarMass} g/mol = ${(molesVal * molarMass).toFixed(4)} g` };
          break;
        }
        case "moles_to_particles": {
          const molesVal = parseFloat(moles);
          if (!moles || isNaN(molesVal) || molesVal <= 0) { setError("Veuillez entrer un nombre de moles valide."); setResult(null); return; }
          resultData = { moles: molesVal, particles: molesVal * AVOGADRO, volumeSTP: molesVal * MOLAR_VOLUME_STP, mass: molarMass > 0 ? molesVal * molarMass : null, molarMass: molarMass > 0 ? molarMass : null, formula: `N = ${molesVal}  6,02210 = ${(molesVal * AVOGADRO).toExponential(4)}` };
          break;
        }
        case "moles_to_volume": {
          const molesVal = parseFloat(moles);
          if (!moles || isNaN(molesVal) || molesVal <= 0) { setError("Veuillez entrer un nombre de moles valide."); setResult(null); return; }
          resultData = { moles: molesVal, particles: molesVal * AVOGADRO, volumeSTP: molesVal * MOLAR_VOLUME_STP, mass: molarMass > 0 ? molesVal * molarMass : null, molarMass: molarMass > 0 ? molarMass : null, formula: `V = ${molesVal} mol  22,4 L/mol = ${(molesVal * MOLAR_VOLUME_STP).toFixed(4)} L` };
          break;
        }
        default: throw new Error("Mthode de calcul inconnue");
      }

      if (resultData) {
        resultData.substanceName = substance === "custom" ? "Substance personnalise" : COMMON_SUBSTANCES[substance]?.name || "";
        resultData.methodName = CALC_METHODS[calcMethod];
        setResult(resultData);
      }
    } catch (err) {
      setError("Erreur dans le calcul : " + err.message);
      setResult(null);
    }
  };

  const handleClear = () => {
    setMass(""); setMoles(""); setParticles(""); setVolume(""); setCustomMolarMass(""); setResult(null); setError("");
  };

  const needsMolarMass = ["mass_to_moles","moles_to_mass","particles_to_moles","volume_to_moles","moles_to_particles","moles_to_volume"].includes(calcMethod);

  const renderInputFields = () => {
    switch (calcMethod) {
      case "mass_to_moles":
        return (
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <label>Masse (grammes)</label>
              <input type="number" className="form-control" value={mass} onChange={(e) => setMass(e.target.value)} min="0" step="0.001" placeholder="Entrez la masse en grammes" />
            </div>
            <div className="col-sm-12 col-md-6">
              <label>Masse molaire (g/mol)</label>
              <input type="number" className="form-control" value={getMolarMass() || ""} readOnly={substance !== "custom"} onChange={(e) => substance === "custom" && setCustomMolarMass(e.target.value)} min="0" step="0.001" placeholder="Masse molaire" style={substance !== "custom" ? { backgroundColor: "#f0f9f4" } : { backgroundColor: "#fff3cd" }} />
            </div>
          </div>
        );
      case "particles_to_moles":
        return (
          <div className="row">
            <div className="col-sm-12">
              <label>Nombre de particules (atomes / molcules / ions)</label>
              <input type="number" className="form-control" value={particles} onChange={(e) => setParticles(e.target.value)} min="0" step="any" placeholder="Ex : 3.011e23" />
              <small className="text-muted">Nombre d'Avogadro = 6,022  10 particules/mol</small>
            </div>
          </div>
        );
      case "volume_to_moles":
        return (
          <div className="row">
            <div className="col-sm-12">
              <label>Volume du gaz  STP (litres)</label>
              <input type="number" className="form-control" value={volume} onChange={(e) => setVolume(e.target.value)} min="0" step="0.001" placeholder="Entrez le volume en litres" />
              <small className="text-muted">Volume molaire  STP = 22,4 L/mol (0 C, 1 atm)</small>
            </div>
          </div>
        );
      case "moles_to_mass":
        return (
          <div className="row">
            <div className="col-sm-12 col-md-6">
              <label>Nombre de moles (mol)</label>
              <input type="number" className="form-control" value={moles} onChange={(e) => setMoles(e.target.value)} min="0" step="0.0001" placeholder="Entrez le nombre de moles" />
            </div>
            <div className="col-sm-12 col-md-6">
              <label>Masse molaire (g/mol)</label>
              <input type="number" className="form-control" value={getMolarMass() || ""} readOnly={substance !== "custom"} onChange={(e) => substance === "custom" && setCustomMolarMass(e.target.value)} min="0" step="0.001" placeholder="Masse molaire" style={substance !== "custom" ? { backgroundColor: "#f0f9f4" } : { backgroundColor: "#fff3cd" }} />
            </div>
          </div>
        );
      case "moles_to_particles":
      case "moles_to_volume":
        return (
          <div className="row">
            <div className="col-sm-12">
              <label>Nombre de moles (mol)</label>
              <input type="number" className="form-control" value={moles} onChange={(e) => setMoles(e.target.value)} min="0" step="0.0001" placeholder="Entrez le nombre de moles" />
            </div>
          </div>
        );
      default: return null;
    }
  };

  return (
    <div className="ltr">
      <SharedHelmet TITLE={TITLE} DESCRIPTION={DESCRIPTION} KEYWORDS={KEYWORDS} OG_URL={OG_URL} />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div className="bg-success text-white text-center" style={{ padding: "15px", borderRadius: "5px" }}>
            <h1 style={{ fontSize: "22px", margin: 0 }}>Calculatrice de moles chimiques</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                La <strong>calculatrice de moles</strong> est un outil gratuit qui vous permet de{" "}
                <strong>calculer le nombre de moles</strong> de trois manires :  partir de la masse et de la
                masse molaire,  partir du nombre de particules (nombre d'Avogadro) ou  partir du volume d'un
                gaz aux conditions normales (STP). Idal pour les lves, tudiants et enseignants en chimie.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>Calculez le nombre de moles facilement</h2>
              </div>

              <div className="row">
                <div className="col-sm-12">
                  <label htmlFor="calcMethod">Mthode de calcul</label>
                  <select id="calcMethod" className="form-select" value={calcMethod} onChange={(e) => { setCalcMethod(e.target.value); handleClear(); }}>
                    {Object.entries(CALC_METHODS).map(([key, value]) => (
                      <option key={key} value={key}>{value}</option>
                    ))}
                  </select>
                </div>
              </div>

              {needsMolarMass && (
                <div className="row mt-2">
                  <div className="col-sm-12">
                    <label htmlFor="substance">Substance chimique</label>
                    <select id="substance" className="form-select" value={substance} onChange={(e) => setSubstance(e.target.value)}>
                      {Object.entries(COMMON_SUBSTANCES).map(([key, { name, molarMass }]) => (
                        <option key={key} value={key}>{name}{molarMass > 0 ? ` (${molarMass} g/mol)` : " (entrez la masse molaire)"}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {substance === "custom" && needsMolarMass && (
                <div className="row mt-2">
                  <div className="col-sm-12">
                    <label htmlFor="customMolarMass">Masse molaire personnalise (g/mol)</label>
                    <input id="customMolarMass" type="number" className="form-control" value={customMolarMass} onChange={(e) => setCustomMolarMass(e.target.value)} min="0" step="0.001" placeholder="Entrez la masse molaire de la substance" style={{ backgroundColor: "#fff3cd" }} />
                  </div>
                </div>
              )}

              <div className="mt-3">{renderInputFields()}</div>

              <div className="text-center mb-3 mt-4">
                <button onClick={calculateResult} className="btn btn-success me-2" style={{ fontSize: "16px", padding: "10px 30px" }}>Calculer</button>
                <button onClick={handleClear} className="btn btn-secondary" style={{ fontSize: "16px", padding: "10px 30px" }}>Effacer</button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>Attention :</strong> {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>Rsultat :</strong>
                  </h3>

                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Grandeur</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>Valeur</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>Nombre de moles</td>
                          <td style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: "#1a5f3f" }}>{formatScientific(result.moles)} mol</td>
                        </tr>
                        {result.mass !== null && result.mass !== undefined && (
                          <tr>
                            <td style={{ textAlign: "center" }}>Masse</td>
                            <td style={{ textAlign: "center", fontSize: "16px", fontWeight: "bold", color: "#0f3d28" }}>{formatScientific(result.mass)} g</td>
                          </tr>
                        )}
                        <tr>
                          <td style={{ textAlign: "center" }}>Nombre de particules</td>
                          <td style={{ textAlign: "center", fontSize: "15px" }}>{result.particles.toExponential(4)} particules</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Volume  STP</td>
                          <td style={{ textAlign: "center", fontSize: "15px" }}>{formatScientific(result.volumeSTP)} L</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header"><strong>Rsultats dtaills</strong></div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                          <li>?? <strong>Nombre de moles :</strong> {formatScientific(result.moles)} mol</li>
                          {result.mass !== null && result.mass !== undefined && (
                            <li>?? <strong>Masse :</strong> {formatScientific(result.mass)} g</li>
                          )}
                          <li>?? <strong>Nombre de particules :</strong> {result.particles.toExponential(4)}</li>
                          <li>?? <strong>Volume  STP :</strong> {formatScientific(result.volumeSTP)} L</li>
                          {result.molarMass && (
                            <li>?? <strong>Masse molaire :</strong> {result.molarMass} g/mol</li>
                          )}
                        </ul>
                        <div className="alert alert-info mt-3" style={{ fontSize: "14px" }}>
                          <strong>?? Substance :</strong> {result.substanceName}
                        </div>
                      </div>
                    </div>

                    <div className="info-section">
                      <div className="info-section-header"><strong>Dtails du calcul</strong></div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p><strong>Mthode :</strong> {result.methodName}</p>
                          <p><strong>Substance :</strong> {result.substanceName}</p>
                          {result.molarMass && <p><strong>Masse molaire :</strong> {result.molarMass} g/mol</p>}
                          <hr />
                          <p style={{ fontSize: "14px", color: "#555" }}><strong>Formule applique :</strong></p>
                          <p style={{ fontSize: "13px", color: "#666", backgroundColor: "#f8f9fa", padding: "10px", borderRadius: "5px" }}>{result.formula}</p>
                          <p style={{ fontSize: "13px", color: "#666" }}>
                            Nombre d'Avogadro = 6,022  10 particules/mol<br />
                            Volume molaire  STP = 22,4 L/mol
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>

        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">

            <div className="info-section">
              <div className="info-section-header">
                <strong>Qu'est-ce que la calculatrice de moles ?</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  La <strong>calculatrice de moles</strong> est un outil en ligne gratuit qui convertit entre
                  la masse, le nombre de moles, le nombre de particules et le volume de gaz en quelques
                  secondes. La mole (Mole ou mol) est l'unit de mesure fondamentale en chimie : elle
                  reprsente un nombre prcis de particules gal  <strong>6,022  10</strong>, connu sous le
                  nom de nombre d'Avogadro. Utilisez cette calculatrice pour :
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Calculer les moles depuis la masse :</strong> divisez la masse en grammes par la masse molaire.</li>
                  <li><strong>Convertir depuis des particules :</strong> divisez le nombre de particules par le nombre d'Avogadro.</li>
                  <li><strong>Calculer les moles d'un gaz :</strong> divisez le volume en litres par 22,4 L/mol  STP.</li>
                  <li><strong>Conversion inverse :</strong> convertissez les moles en masse, en particules ou en volume.</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>Comment utiliser la calculatrice pas  pas :</h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Choisissez la mthode de calcul dans la liste droulante.</li>
                  <li>Slectionnez la substance chimique (ou "Substance personnalise" pour saisir une masse molaire manuellement).</li>
                  <li>Entrez la valeur connue (masse, moles, particules ou volume).</li>
                  <li>Cliquez sur  Calculer  pour obtenir tous les rsultats simultanment.</li>
                </ol>
                <div className="alert alert-info mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>?? Conseil :</strong> La calculatrice vous donne en une seule fois le nombre de moles,
                  la masse, le nombre de particules et le volume  STP  gagnez du temps et vitez les erreurs de calcul manuel.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Utilisations de la calculatrice de moles</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>En cours et examens :</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Rsolution de problmes :</strong> calcul rapide de moles dans les quations chimiques.</li>
                  <li><strong>Rvision d'examens :</strong> vrification des rsultats avant de rendre votre copie.</li>
                  <li><strong>Comprhension des relations chimiques :</strong> lien entre masse, moles et particules.</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>En laboratoire :</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Prparation de solutions :</strong> dterminez la quantit exacte de substance ncessaire.</li>
                  <li><strong>Calculs de ractions :</strong> dfinissez les quantits de ractifs et produits.</li>
                  <li><strong>Mesure des gaz :</strong> convertissez le volume d'un gaz en moles facilement.</li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>Dans l'industrie chimique :</h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>Contrle des proportions :</strong> calcul prcis des matires premires.</li>
                  <li><strong>Contrle qualit :</strong> vrification de la concentration des produits.</li>
                  <li><strong>Optimisation :</strong> rduction des dchets grce  des calculs prcis.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Formules de calcul du nombre de moles</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>La mole est relie  trois grandeurs fondamentales : la masse, le nombre de particules et le volume du gaz.</p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Loi</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Formule</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>Variables</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>Moles depuis la masse</td><td style={{ textAlign: "center" }}>n = m  M</td><td>n : moles, m : masse (g), M : masse molaire (g/mol)</td></tr>
                    <tr><td>Moles depuis les particules</td><td style={{ textAlign: "center" }}>n = N  N?</td><td>N : nombre de particules, N? = 6,02210</td></tr>
                    <tr><td>Moles depuis le volume</td><td style={{ textAlign: "center" }}>n = V  22,4</td><td>V : volume en litres  STP (0 C, 1 atm)</td></tr>
                    <tr><td>Masse depuis les moles</td><td style={{ textAlign: "center" }}>m = n  M</td><td>n : moles, M : masse molaire (g/mol)</td></tr>
                    <tr><td>Particules depuis les moles</td><td style={{ textAlign: "center" }}>N = n  N?</td><td>n : moles, N? = 6,02210</td></tr>
                    <tr><td>Volume depuis les moles</td><td style={{ textAlign: "center" }}>V = n  22,4</td><td>n : moles, volume en litres  STP</td></tr>
                  </tbody>
                </table>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>Qu'est-ce que la mole exactement ?</h3>
                <p style={{ lineHeight: "1.8" }}>
                  La mole n'est pas juste un nombre  c'est une unit de mesure de quantit, comme la douzaine
                  reprsente 12. Une mole de n'importe quelle substance contient exactement&nbsp;
                  <strong>6,022  10</strong> de ses particules. Ce nombre gigantesque n'est pas arbitraire :
                  c'est le nombre d'atomes contenus dans exactement 12 grammes de carbone-12. Les scientifiques
                  ont choisi cette dfinition pour faire le lien entre la chimie microscopique (atomes) et les
                  mesures de laboratoire (grammes et millilitres).
                </p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>Questions frquentes sur les moles</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "16px" }}>Qu'est-ce qu'une mole en chimie ?</h3>
                <p>Une mole est une unit de quantit de matire du Systme International (SI). Elle contient exactement 6,02214076  10 entits lmentaires (atomes, molcules, ions, etc.).</p>
                <h3 style={{ fontSize: "16px" }}>Quelle est la diffrence entre la mole et la masse molaire ?</h3>
                <p>La mole est une quantit (nombre de particules), tandis que la masse molaire est la masse d'une mole d'une substance donne, exprime en g/mol. Exemple : la masse molaire de l'eau (H2O) est 18,015 g/mol.</p>
                <h3 style={{ fontSize: "16px" }}>Comment calculer le nombre de moles depuis la masse ?</h3>
                <p>Utilisez la formule : n (mol) = m (g)  M (g/mol). Exemple : 36 g d'eau  18,015 g/mol = 1,999 mol  2 mol.</p>
                <h3 style={{ fontSize: "16px" }}>Qu'est-ce que le volume molaire  STP ?</h3>
                <p> conditions normales de temprature et de pression (0 C, 1 atm), une mole de tout gaz idal occupe 22,4 litres. On utilise cette constante pour convertir volume ? moles pour les gaz.</p>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default MolesCalculator;
